import { useCallback, useRef } from 'react';

/** Minimum displacement (px) before locking in a direction. */
const DEFAULT_DIRECTION_THRESHOLD = 10;

/**
 * Pre-computed tan(20°) ≈ 0.364.
 * Gestures within 20° of vertical are treated as scroll;
 * everything else activates horizontal chart scrubbing.
 */
const VERTICAL_TAN = Math.tan((20 * Math.PI) / 180);

type Direction = 'horizontal' | 'vertical' | 'undecided';

interface HorizontalTouchGuardOptions<T extends Element> {
    onTouchStart?: (event: React.TouchEvent<T>) => void;
    onHorizontalMove: (event: React.TouchEvent<T>) => void;
    onTouchEnd?: () => void;
    /** px of movement before direction is decided (default 10) */
    directionThreshold?: number;
}

interface HorizontalTouchGuardHandlers<T extends Element> {
    handleTouchStart: (event: React.TouchEvent<T>) => void;
    handleTouchMove: (event: React.TouchEvent<T>) => void;
    handleTouchEnd: () => void;
}

const useHorizontalTouchGuard = <T extends Element>(
    options: HorizontalTouchGuardOptions<T>,
): HorizontalTouchGuardHandlers<T> => {
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const directionRef = useRef<Direction>('undecided');
    const threshold = options.directionThreshold ?? DEFAULT_DIRECTION_THRESHOLD;

    const handleTouchStart = useCallback((event: React.TouchEvent<T>) => {
        const touch = event.touches[0];
        if (!touch) return;
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        directionRef.current = 'undecided';
    }, []);

    const handleTouchMove = useCallback(
        (event: React.TouchEvent<T>) => {
            const start = touchStartRef.current;
            if (!start) return;

            const touch = event.touches[0];
            if (!touch) return;

            // Still deciding direction — check if we've moved enough
            if (directionRef.current === 'undecided') {
                const dx = Math.abs(touch.clientX - start.x);
                const dy = Math.abs(touch.clientY - start.y);

                if (Math.max(dx, dy) < threshold) return;

                if (dx > dy * VERTICAL_TAN) {
                    directionRef.current = 'horizontal';
                    // Fire the initial touch callback now that we know it's horizontal
                    options.onTouchStart?.(event);
                } else {
                    directionRef.current = 'vertical';
                    return;
                }
            }

            if (directionRef.current === 'vertical') return;

            event.preventDefault();
            event.stopPropagation();
            options.onHorizontalMove(event);
        },
        [options, threshold],
    );

    const handleTouchEnd = useCallback(() => {
        // Only fire onTouchEnd if we actually entered horizontal mode
        if (directionRef.current === 'horizontal') {
            options.onTouchEnd?.();
        }
        touchStartRef.current = null;
        directionRef.current = 'undecided';
    }, [options]);

    return { handleTouchStart, handleTouchMove, handleTouchEnd };
};

export default useHorizontalTouchGuard;
